import { useEffect, useState } from "react";
import { Apple, Smartphone, CreditCard, ShieldCheck, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Pre-Stripe trust block — sits between the email field and the Pay button on
 * the 7days + nabd checkout pages.
 *
 * - Sets correct expectations for what visitors will see on Stripe:
 *   Apple Pay, Google Pay, Visa, Mastercard chips + safety/refund line.
 * - For Gulf visitors (AE/KW/QA/BH/OM/SA) shows a "هل تحتاجين مساعدة؟"
 *   prompt that opens the automated payment-help chatbot mounted by the
 *   parent page (no WhatsApp deep-link — the chatbot mints a fresh Stripe
 *   session on demand and routes the visitor straight back to payment).
 * - For Saudi visitors (SA) specifically — since the product is now priced
 *   in AED and Stripe routes through international Visa/MC rails (Mada is
 *   not supported by Stripe for a UK entity), we surface a small advisory
 *   asking them to use a Visa/MC international card. This was added during
 *   the Feb-2026 UAE pivot to keep KSA traffic informed without blocking it.
 *
 * Props
 *   theme    "gold" | "amber"     Brand accent.
 *   onChatOpen () => void          Optional — when present and the visitor is
 *                                  in a Gulf country, the trust block renders
 *                                  a soft "open the help bot" hint above the
 *                                  Pay button. Clicking it calls this prop.
 */

const GULF = new Set(["SA", "AE", "KW", "QA", "BH", "OM"]);

export default function CheckoutTrustBlock({ theme = "gold", testid = "checkout-trust-block", onChatOpen }) {
  const [country, setCountry] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/geo")
      .then((r) => { if (!cancelled) setCountry((r.data?.country || "").toUpperCase()); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  const palette = theme === "amber"
    ? { accent: "#F4B842", border: "rgba(244,184,66,0.30)", bg: "rgba(244,184,66,0.06)", text: "#9AA5B1", title: "#E8ECEF", chipBg: "#0B1018", chipBorder: "rgba(255,255,255,0.10)" }
    : { accent: "#C9A66A", border: "rgba(201,166,106,0.30)", bg: "rgba(201,166,106,0.06)", text: "#A7956E", title: "#F5EAD3", chipBg: "#0A0606", chipBorder: "rgba(201,166,106,0.20)" };

  const showHelpHint = GULF.has(country) && typeof onChatOpen === "function";
  const showKsaNotice = country === "SA";

  return (
    <div className="mt-5 space-y-3" data-testid={testid}>
      {/* Payment-methods reassurance */}
      <div
        className="rounded-xl p-3.5 border"
        style={{ background: palette.bg, borderColor: palette.border }}
        data-testid={`${testid}-methods`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="leading-tight">
            <div className="text-[10.5px] tracking-[0.22em] font-bold mb-0.5" style={{ color: palette.accent }}>
              طرق الدفع المتاحة
            </div>
            <div className="text-[12.5px] font-bold" style={{ color: palette.title }}>
              Apple Pay · بطاقات Visa · Mastercard
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold rounded-md px-2 py-1.5 border" style={{ background: palette.chipBg, borderColor: palette.chipBorder, color: palette.title }}>
              <Apple className="w-3 h-3" /> Pay
            </span>
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold rounded-md px-2 py-1.5 border" style={{ background: palette.chipBg, borderColor: palette.chipBorder, color: palette.title }}>
              <Smartphone className="w-3 h-3" /> G Pay
            </span>
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold rounded-md px-2 py-1.5 border" style={{ background: palette.chipBg, borderColor: palette.chipBorder, color: palette.title }}>
              <CreditCard className="w-3 h-3" />
            </span>
          </div>
        </div>
        <div className="mt-2 text-[11px] flex items-center gap-1.5" style={{ color: palette.text }}>
          <ShieldCheck className="w-3 h-3" style={{ color: palette.accent }} />
          دفع آمن عبر Stripe · ضمان استرداد ٧ أيام · بدون تجديد تلقائي
        </div>
      </div>

      {/* KSA-specific Mada advisory — Stripe processes UAE cards natively but
          routes Mada through international Visa/MC rails (which the issuer may
          decline). We ask Saudi visitors to use an international card. */}
      {showKsaNotice && (
        <div
          className="rounded-xl p-3 border text-[11.5px] leading-[1.85]"
          style={{ background: "rgba(244,184,66,0.06)", borderColor: "rgba(244,184,66,0.30)", color: palette.title }}
          data-testid={`${testid}-ksa-notice`}
        >
          <span className="font-bold" style={{ color: palette.accent }}>للعملاء من السعودية:</span>{" "}
          الرجاء استخدام بطاقة <span className="font-bold">Visa</span> أو <span className="font-bold">Mastercard</span> دولية (وليس بطاقة مدى) لإتمام الدفع بنجاح.
        </div>
      )}

      {/* Gulf-only: tap-to-open chatbot pill — bot answers payment questions
          and regenerates a fresh Stripe session on demand. */}
      {showHelpHint && (
        <button
          type="button"
          onClick={onChatOpen}
          data-testid={`${testid}-chat-cta`}
          className="w-full block rounded-xl border p-3 text-right transition-colors"
          style={{ background: "rgba(37,211,102,0.08)", borderColor: "rgba(37,211,102,0.30)" }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(37,211,102,0.15)", border: "1px solid rgba(37,211,102,0.40)" }}>
              <MessageCircle className="w-4 h-4 text-[#25D366]" strokeWidth={2.2} />
            </div>
            <div className="flex-1 leading-tight">
              <div className="text-[12.5px] font-bold" style={{ color: palette.title }}>
                هل واجهتِ مشكلة في الدفع؟
              </div>
              <div className="text-[11px]" style={{ color: palette.text }}>
                افتحي المحادثة وراح نساعدكِ خلال ثوانٍ — رد آلي ٢٤/٧
              </div>
            </div>
            <div className="text-[11px] font-bold text-[#25D366] shrink-0">
              افتح المحادثة →
            </div>
          </div>
        </button>
      )}
    </div>
  );
}
